import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { of, Subject } from 'rxjs'
import { filter } from 'rxjs/operators'
import { PlateChooseComponent } from 'src/app/shared/components/dialog/components/plate-choose/plate-choose/plate-choose.component'
import { ProfileChooseComponent } from 'src/app/shared/components/dialog/components/profile-choose/profile-choose.component'
import { ProfileChooseService } from 'src/app/shared/components/dialog/components/profile-choose/service/profile-choose.service'
import { SalesChooseComponent } from 'src/app/shared/components/dialog/components/sales-choose/sales-choose.component'
import { DialogService } from 'src/app/shared/components/dialog/service/dialog.service'
import { SubSink } from 'subsink'

import { SharedService } from '../../shared.service'
import { StorageService } from '../../storage/storage.service'
import { UserInfo } from '../../user/model/fullInfo'
import { UserService } from '../../user/user.service'
import { CustomerServiceRole, Profile } from '../model/profile'
import { StoreManagementService } from '../store-management.service'

@Injectable({
	providedIn: 'root',
})
export class ProfileManagementService {
	private readonly changeProfile$: Subject<any> = new Subject<any>()

	private subs = new SubSink()
	constructor(
		private dialogService: DialogService,
		private storeManagement: StoreManagementService,
		private storageService: StorageService,
		private profileChooseService: ProfileChooseService,
		private router: Router,
		private userService: UserService,
		private sharedService: SharedService
	) {}

	get changeProfile(): any {
		return this.changeProfile$.asObservable()
	}
	setChangeProfile(value): void {
		this.changeProfile$.next(value)
	}

	getCustomerServiceRole() {
		return of(CustomerServiceRole)
	}

	setProfilesInStore(profiles) {
		this.storageService.setSessionStorage('profiles', JSON.stringify(profiles))
	}

	setProfileInStore(profileSelected): void {
		this.storageService.setSessionStorage('profile', JSON.stringify(profileSelected))
	}

	
	setProfileWebForm(profile:any): void {
		this.storageService.setSessionStorage('profileWebForm', JSON.stringify(profile))
	}


	setAssCode(asscode): void {
		if (!!asscode) {
			this.storageService.setSessionStorage('asscode', asscode)
		}
	}

	setMenu(menu): void {
		this.storageService.setSessionStorage('menu', JSON.stringify(menu))
	}

	getMenu(): any[] {
		return !!this.storageService.getSessionStorage('menu')
			? JSON.parse(this.storageService.getSessionStorage('menu'))
			: null
	}

	getAsscode(): string {
		return this.storageService.getSessionStorage('asscode')
	}

	getProfileByStore(): Profile {
		return JSON.parse(this.storageService.getSessionStorage('profile'))
	}
	getProfileByStoreWebForm(): Profile {
		return JSON.parse(this.storageService.getSessionStorage('profileWebForm'))
	}
	getProfilesByStore(): Profile {
		return JSON.parse(this.storageService.getSessionStorage('profiles'))
	}

	// Ri-idrata lo stato IN MEMORIA partendo dal profilo gia' presente in sessione.
	// Serve dopo il ripristino di un logout annullato: sessionStorage torna a posto, ma
	// header e menu utente non leggono lo storage, ascoltano questi due store. Senza
	// questa notifica l'header resta su 'User' e il menu profilo resta ridotto.
	rehydrateProfileFromStore(): void {
		const profile = this.getProfileByStore()
		if (!profile) {
			return
		}
		this.storeManagement.setUserInfo(profile)
		this.profileChooseService.setNewProfile(profile)
	}

	conditionForChooseProfile(profiles): boolean {
		return profiles.length > 1 || profiles[0].roles.length > 1
	}

	openDialogForChooseProfile(isMobile, profiles, closable = false): any {
		return this.dialogService.onOpenDialogResponsive(isMobile, ProfileChooseComponent, {
			profiles,
			closable,
		})
	}

	openDialogForManageSales(isMobile, profiles, closable = false): any {
		return this.dialogService.onOpenDialogResponsive(
			isMobile,
			SalesChooseComponent,
			{
				profiles,
				closable,
			},
			98
		)
	}

	removeProfileFromStore(): void {
		this.storageService.setSessionStorage('profile', null)
	}

	setProfile(isMobile): void {
		console.log("Inizio setProfile. isMobile:", isMobile);
		this.subs.add(
			this.userService
				.getContactProfile()
				.pipe(filter((res: any) => !!res?.profiles[0]))
				.subscribe((res: any) => {
					console.log("Profili ricevuti:", res.profiles);
					const profiles = res.profiles
					this.setProfilesInStore(profiles)
					if (this.conditionForChooseProfile(profiles)) {
						console.log("Condizione modale verificata: APERTURA MODALE");
						this.openDialogForChooseProfile(isMobile, profiles).subscribe((profileSelected) => {
							this.setAndCheckProfile(profileSelected, isMobile)
							const cs_externalId = sessionStorage.getItem('cs_externalId')
							console.log("cs_external,",cs_externalId )
							// Il contactId da inviare e' quello del contatto scelto nella modale:
							if (cs_externalId == null && !!profileSelected?.contactId) {
								this.updateLastLogin(profileSelected.contactId)
							}
						})
					} else {
						console.log("Condizione modale fallita: LOGIN AUTOMATICO");
						this.setOnlyProfileAvailable(profiles)
						this.setAndCheckProfile(profiles[0], isMobile)
						const cs_externalId = sessionStorage.getItem('cs_externalId')
						console.log("cs_external,",cs_externalId )
						if (cs_externalId==null){
							this.updateLastLogin(profiles[0].contactId)
						}
					}
				})
		)
	}

	manageSales(profileSelected, isMobile?) {
		if (profileSelected.isSales || profileSelected.role == '11') {
			// Guard against opening more than one sales-choose dialog: during login the
			// guard and post-login flow both call this while salesSetted is still falsy,
			// which otherwise stacks identical dialogs (user has to click through each).
			const alreadyOpen = this.dialogService.dialog.openDialogs.some(
				(ref) => ref.componentInstance instanceof SalesChooseComponent
			)
			if (!alreadyOpen) {
				this.openDialogForManageSales(isMobile, profileSelected)
			}
		}
	}

	setAndCheckProfile(profileSelected, isMobile?, isCs?): void {
		this.checkIfRegisteredAndStoreProfileSelected(profileSelected, isCs)
		//this.verifyDelegationForRenderPage(profileSelected);
	}

	verifyDelegationForRenderPage(profileSelected: Profile): void {
		if (
			!profileSelected.statoDelega &&
			profileSelected.delegaArt94LastUpdate === '' &&
			!!!profileSelected.isSales &&
			(profileSelected.role == '2' || profileSelected.role == '10')
		) {
			this.router.navigate(['/tools-service/delegation-ald-art94'])
		}
	}

	checkIfRegisteredAndStoreProfileSelected(profileSelected, isCs?): void {
		this.checkIfThereAreMoreVehicleAndSelectedOneOfThem(profileSelected, isCs)
	}

	//VECCHIA API V1 MY CONTRACTS SOSTITUITA DALLA V2 SOTTO
	// checkIfThereAreMoreVehicleAndSelectedOneOfThem(profileSelected: Profile, isCs?): void {
	// 	if (profileSelected.role == '1' || profileSelected.role == '5' || profileSelected.role == '8') {
	// 		this.sharedService.getMyContracts(profileSelected).subscribe((res) => {
	// 			const listVehicle = res.contract_history
	// 			if (listVehicle.length > 1) {
	// 				this.chooseVehicleToManage(null, listVehicle).subscribe((vehicleChoosen: any) => {
	// 					profileSelected.plate = vehicleChoosen[0].plate
	// 					profileSelected.contractId = JSON.stringify(vehicleChoosen[0].id_contract)
	// 					profileSelected.isMulti = true
	// 					profileSelected.valid_from = vehicleChoosen[0]?.valid_from;
	// 					this.storePorfileInSession(profileSelected, isCs)
	// 				})
	// 			} else {
	// 				profileSelected.valid_from = res.contract_history[0]?.valid_from;
	// 				this.storePorfileInSession(profileSelected, isCs)
	// 			}
	// 		})
	// 	} else {
	// 		this.storePorfileInSession(profileSelected, isCs)
	// 	}
	// }

	checkIfThereAreMoreVehicleAndSelectedOneOfThem(profileSelected: Profile, isCs?): void {
		if (profileSelected.role == '1' || profileSelected.role == '5' || profileSelected.role == '8' || profileSelected.role == '400038') {
			this.sharedService.getMyContractsV2(profileSelected).subscribe((res) => {
				const listVehicle = res.contract_history
				if (listVehicle.length > 1) {
					this.chooseVehicleToManage(null, listVehicle).subscribe((vehicleChoosen: any) => {
						profileSelected.plate = vehicleChoosen[0].plate
						profileSelected.contractId = JSON.stringify(vehicleChoosen[0].id_contract)
						profileSelected.isMulti = true
						profileSelected.valid_from = vehicleChoosen[0]?.valid_from;
						profileSelected.quoteId = vehicleChoosen[0]?.quote_id;
						this.storePorfileInSession(profileSelected, isCs)
					})
				} else {
					profileSelected.valid_from = res.contract_history[0]?.valid_from;
					this.storePorfileInSession(profileSelected, isCs)
				}
			})
		} else {
			this.storePorfileInSession(profileSelected, isCs)
		}
	}

	storePorfileInSession(profileSelected, isCs?): any {
		this.manageProfileStartingFromService(profileSelected)
		this.checkIfRegistered(profileSelected)
		this.storeManagement.setUserInfo(profileSelected)
		this.setProfileInStore(profileSelected)
		sessionStorage.setItem('profile', JSON.stringify(profileSelected))
		this.profileChooseService.setNewProfile(profileSelected)
		this.setUserLogAndDirectToHome(profileSelected, isCs)
	}

	manageProfileStartingFromService(profileSelected): any {
		profileSelected.lc = profileSelected.leasing_companies[0].id
	}

	chooseVehicleToManage(isMobile, vehicles, closable = false): any {
		return this.dialogService.onOpenDialogResponsive(isMobile, PlateChooseComponent, {
			vehicles,
			closable,
		})
	}

	setUserLogAndDirectToHome(profileSelected, isCs?) {
		const userInfo = {} as UserInfo
		userInfo.customerId = profileSelected.customerId
		userInfo.customerRef = profileSelected.customerReference
		userInfo.contactId = profileSelected.contactId
		userInfo.role = !!profileSelected.mappingMiles ? profileSelected.mappingMiles : profileSelected.role
		this.userService.userLog(userInfo).subscribe((res) => {
			this.checkIfSalesIsSetted()
			this.router.navigate(['/home'])

		})
	}

	checkIfSalesIsSetted() {
		const profile = this.getProfileByStore()
		if ((profile?.isSales || profile?.isBroker) && !profile.salesSetted) {
			this.manageSales(profile)
		}
	}

	checkIfRegistered(profileSelected): void {
		if (!!!profileSelected.registrationDate) {
			//this.router.navigate(["/user/registration"]);
		} else {
			this.verifyDelegationForRenderPage(profileSelected)
		}
	}

	isUserRegistered(): boolean {
		const profile = this.getProfileByStore()
		if (profile?.registrationDate === null) {
			return true
		}
		return !!profile?.registrationDate
	}

	setOnlyProfileAvailable(profiles): void {
		const singleProfile = profiles[0]
		singleProfile.role = profiles[0].roles[0]?.roleId
		singleProfile.roleName = profiles[0].roles[0].roleName
	}

	setOnlyProfileAvailableCs(profiles): void {
		const singleProfile = profiles[0]

		singleProfile.role = profiles[0].roles[0]?.roleId
		singleProfile.roleName = profiles[0].roles[0].roleName
	}

	getProfileFromSession(): void {}

	updateLastLogin(contactId){
		console.log("contact:",contactId)
		this.subs.add(
			this.userService.updateLastLogin(contactId).subscribe((res) => 
				console.log("res, aggiornato",res)
			)
		)
	}

}
